import React, { useEffect, useState, useCallback } from 'react';
import {
  getDriverCoachingRequests,
  updateDriverCoachingStatus,
  DRIVER_COACHING_STATUSES,
  DRIVER_COACHING_FILTER_STATUSES,
  DRIVER_COACHING_STATUS_PARTNER_DRIVER,
  DRIVER_COACHING_PARTNERS,
} from '../../services/DriverCoachingService';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Pagination from '../../utils/Pagination';
import Footer from '../../Footer';

function useDebounce(value, delay) {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(timer);
    };
  }, [value, delay]);

  return debouncedValue;
}

const STATUS_COLORS = {
  NEW: { bg: '#ffc107', text: '#000' },
  IN_PROGRESS: { bg: '#0d6efd', text: '#fff' },
  DONE: { bg: '#198754', text: '#fff' },
  [DRIVER_COACHING_STATUS_PARTNER_DRIVER]: { bg: '#6f42c1', text: '#fff' },
};

const STATUS_LABELS = {
  NEW: 'New',
  IN_PROGRESS: 'In Progress',
  DONE: 'Done',
  [DRIVER_COACHING_STATUS_PARTNER_DRIVER]: 'Partner Driver',
};

function DriverCoaching() {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [currentPage, setCurrentPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [pageSize, setPageSize] = useState(25);

  const [statusFilter, setStatusFilter] = useState('');
  const [btParcelIdFilter, setBtParcelIdFilter] = useState('');
  const [emailFilter, setEmailFilter] = useState('');
  const [metroCodeFilter, setMetroCodeFilter] = useState('');
  const [partnerFilter, setPartnerFilter] = useState('');
  const [fromDateFilter, setFromDateFilter] = useState('');
  const [toDateFilter, setToDateFilter] = useState('');

  const [updatingId, setUpdatingId] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [filtersOpen, setFiltersOpen] = useState(false);

  const debouncedBtParcelId = useDebounce(btParcelIdFilter, 500);
  const debouncedEmail = useDebounce(emailFilter, 500);
  const debouncedMetroCode = useDebounce(metroCodeFilter, 500);

  const fetchRequests = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getDriverCoachingRequests({
        page: currentPage,
        pageSize,
        status: statusFilter,
        btParcelId: debouncedBtParcelId,
        submittedByEmail: debouncedEmail,
        metroCode: debouncedMetroCode,
        partner: partnerFilter,
        fromDate: fromDateFilter,
        toDate: toDateFilter,
      });
      setRequests(data.items || []);
      setTotalItems(data.totalCount || 0);
    } catch (err) {
      setError('Failed to load driver coaching requests');
      toast.error('Failed to load driver coaching requests');
      setRequests([]);
      setTotalItems(0);
    }
    setLoading(false);
  }, [
    currentPage,
    pageSize,
    statusFilter,
    debouncedBtParcelId,
    debouncedEmail,
    debouncedMetroCode,
    partnerFilter,
    fromDateFilter,
    toDateFilter,
  ]);

  useEffect(() => {
    fetchRequests();
  }, [fetchRequests]);

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  const handlePageSizeChange = (e) => {
    setPageSize(Number(e.target.value));
    setCurrentPage(1);
  };

  // Wraps a state setter so any filter change also resets to the first page.
  const withPageReset = (setter) => (value) => {
    setter(value);
    setCurrentPage(1);
  };

  const handleStatusFilterChange = (e) => withPageReset(setStatusFilter)(e.target.value);
  const handleBtParcelIdChange = (e) => withPageReset(setBtParcelIdFilter)(e.target.value);
  const handleEmailChange = (e) => withPageReset(setEmailFilter)(e.target.value);
  const handleMetroCodeChange = (e) => withPageReset(setMetroCodeFilter)(e.target.value);
  const handlePartnerChange = (e) => withPageReset(setPartnerFilter)(e.target.value);
  const handleFromDateChange = (e) => withPageReset(setFromDateFilter)(e.target.value);
  const handleToDateChange = (e) => withPageReset(setToDateFilter)(e.target.value);

  const clearFilters = () => {
    setStatusFilter('');
    setBtParcelIdFilter('');
    setEmailFilter('');
    setMetroCodeFilter('');
    setPartnerFilter('');
    setFromDateFilter('');
    setToDateFilter('');
    setCurrentPage(1);
  };

  const handleStatusUpdate = async (id, newStatus) => {
    setUpdatingId(id);
    try {
      await updateDriverCoachingStatus(id, newStatus);
      toast.success('Status updated');
      setRequests((prev) =>
        prev.map((r) => (r.id === id ? { ...r, status: newStatus } : r))
      );
    } catch (err) {
      toast.error(err?.error || 'Failed to update status');
    }
    setUpdatingId(null);
  };

  const toggleExpanded = (id) => {
    setExpandedId((prev) => (prev === id ? null : id));
  };

  const totalPages = Math.ceil(totalItems / pageSize);

  const getStatusBadge = (status) => {
    const colors = STATUS_COLORS[status] || { bg: '#6c757d', text: '#fff' };
    return (
      <span
        className="badge"
        style={{
          backgroundColor: colors.bg,
          color: colors.text,
          padding: '0.4em 0.8em',
          borderRadius: '0.25rem',
        }}
      >
        {STATUS_LABELS[status] || status}
      </span>
    );
  };

  const activeFilters = [
    statusFilter,
    btParcelIdFilter,
    emailFilter,
    metroCodeFilter,
    partnerFilter,
    fromDateFilter,
    toDateFilter,
  ].filter(Boolean);
  const activeFilterCount = activeFilters.length;
  const hasActiveFilters = activeFilterCount > 0;

  return (
    <div style={{ paddingLeft: 25, paddingRight: 25, width: '100%', minHeight: '100vh' }} className="mt-4">
      <ToastContainer />

      <div className="d-flex justify-content-between align-items-center mb-4">
        <h4 className="mb-0">Driver Coaching Requests</h4>
        <div className="d-flex align-items-center gap-2">
          <button
            className={`btn d-flex align-items-center ${hasActiveFilters ? 'btn-primary' : 'btn-outline-secondary'}`}
            onClick={() => setFiltersOpen(true)}
            title="Filters"
          >
            <i className="bi bi-funnel me-2"></i>
            Filters
            {hasActiveFilters && (
              <span className="badge bg-light text-dark ms-2">{activeFilterCount}</span>
            )}
          </button>
          <button
            className="btn btn-outline-secondary d-flex align-items-center"
            onClick={fetchRequests}
            disabled={loading}
            title="Refresh"
          >
            {loading ? (
              <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
            ) : (
              <i className="bi bi-arrow-clockwise me-2"></i>
            )}
            Refresh
          </button>
        </div>
      </div>

      {/* Filters slide-over panel */}
      {filtersOpen && (
        <>
          <div
            className="modal-backdrop fade show"
            onClick={() => setFiltersOpen(false)}
            style={{ zIndex: 1040 }}
          ></div>
          <aside
            style={{
              position: 'fixed',
              top: 0,
              right: 0,
              bottom: 0,
              width: 360,
              maxWidth: '90vw',
              background: '#fff',
              boxShadow: '-4px 0 16px rgba(0, 0, 0, 0.15)',
              zIndex: 1045,
              display: 'flex',
              flexDirection: 'column',
            }}
            role="dialog"
            aria-label="Filters"
          >
            <div className="d-flex justify-content-between align-items-center px-3 py-3 border-bottom">
              <span className="fw-bold">
                <i className="bi bi-funnel me-2"></i>Filters
                {hasActiveFilters && (
                  <span className="badge bg-primary ms-2">{activeFilterCount}</span>
                )}
              </span>
              <div className="d-flex align-items-center gap-2">
                {hasActiveFilters && (
                  <button className="btn btn-sm btn-link p-0 text-decoration-none" onClick={clearFilters}>
                    Clear all
                  </button>
                )}
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setFiltersOpen(false)}
                  aria-label="Close"
                ></button>
              </div>
            </div>
            <div className="p-3" style={{ overflowY: 'auto', flexGrow: 1 }}>
              <div className="mb-3">
                <label className="form-label fw-bold small text-muted">Status</label>
                <select
                  className="form-select"
                  value={statusFilter}
                  onChange={handleStatusFilterChange}
                  disabled={loading}
                >
                  <option value="">All Statuses</option>
                  {DRIVER_COACHING_FILTER_STATUSES.map((s) => (
                    <option key={s} value={s}>{STATUS_LABELS[s] || s}</option>
                  ))}
                </select>
              </div>

              <div className="mb-3">
                <label className="form-label fw-bold small text-muted">Date Range</label>
                <div className="d-flex flex-column gap-2">
                  <div className="input-group input-group-sm">
                    <span className="input-group-text">From</span>
                    <input
                      type="date"
                      className="form-control"
                      value={fromDateFilter}
                      max={toDateFilter || undefined}
                      onChange={handleFromDateChange}
                      disabled={loading}
                    />
                  </div>
                  <div className="input-group input-group-sm">
                    <span className="input-group-text">To</span>
                    <input
                      type="date"
                      className="form-control"
                      value={toDateFilter}
                      min={fromDateFilter || undefined}
                      onChange={handleToDateChange}
                      disabled={loading}
                    />
                  </div>
                </div>
              </div>

              <div className="mb-3">
                <label className="form-label fw-bold small text-muted">Metro Code</label>
                <div className="input-group">
                  <span className="input-group-text">
                    <i className="bi bi-geo-alt"></i>
                  </span>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="e.g. ATL-1"
                    value={metroCodeFilter}
                    onChange={handleMetroCodeChange}
                    disabled={loading}
                  />
                </div>
              </div>

              <div className="mb-3">
                <label className="form-label fw-bold small text-muted">Partner</label>
                <select
                  className="form-select"
                  value={partnerFilter}
                  onChange={handlePartnerChange}
                  disabled={loading}
                >
                  <option value="">All Partners</option>
                  {DRIVER_COACHING_PARTNERS.map((p) => (
                    <option key={p} value={p}>{p}</option>
                  ))}
                </select>
              </div>

              <div className="mb-3">
                <label className="form-label fw-bold small text-muted">BT Parcel ID</label>
                <div className="input-group">
                  <span className="input-group-text">
                    <i className="bi bi-search"></i>
                  </span>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="BTP_..."
                    value={btParcelIdFilter}
                    onChange={handleBtParcelIdChange}
                    disabled={loading}
                  />
                </div>
              </div>

              <div className="mb-1">
                <label className="form-label fw-bold small text-muted">Submitter Email</label>
                <div className="input-group">
                  <span className="input-group-text">
                    <i className="bi bi-envelope"></i>
                  </span>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="user@bettertrucks.com"
                    value={emailFilter}
                    onChange={handleEmailChange}
                    disabled={loading}
                  />
                </div>
              </div>
            </div>
          </aside>
        </>
      )}

      <div>
          {loading && requests.length === 0 ? (
            <div className="text-center py-4">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          ) : error ? (
            <div className="alert alert-danger">{error}</div>
          ) : (
            <>
              <div className="table-responsive">
                <table className="table table-hover align-middle">
                  <thead>
                    <tr>
                      <th style={{ width: 40 }}></th>
                      <th>BT Parcel ID</th>
                      <th>Driver</th>
                      <th>Metro</th>
                      <th>Reason</th>
                      <th>Submitted By</th>
                      <th>Status</th>
                      <th>Created</th>
                      <th style={{ width: 180 }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {requests.length === 0 ? (
                      <tr>
                        <td colSpan="9" className="text-center text-muted py-4">
                          No driver coaching requests found
                        </td>
                      </tr>
                    ) : (
                      requests.map((req) => {
                        const isExpanded = expandedId === req.id;
                        const isPartnerDriver = req.status === DRIVER_COACHING_STATUS_PARTNER_DRIVER;
                        return (
                          <React.Fragment key={req.id}>
                            <tr style={{ cursor: 'pointer' }} onClick={() => toggleExpanded(req.id)}>
                              <td>
                                <i className={`bi ${isExpanded ? 'bi-chevron-down' : 'bi-chevron-right'}`}></i>
                              </td>
                              <td>
                                <span className="fw-semibold font-monospace">{req.btParcelId || '-'}</span>
                              </td>
                              <td>{req.coachingDriverFullName || '-'}</td>
                              <td>{req.metroCode || '-'}</td>
                              <td>{req.coachingReason || '-'}</td>
                              <td>
                                <div>{req.submittedByFullName || '-'}</div>
                                {req.submittedByEmailAddress && (
                                  <small className="text-muted">{req.submittedByEmailAddress}</small>
                                )}
                                {req.source && (
                                  <small className="badge bg-light text-dark border d-inline-block mt-1">
                                    via {req.source}
                                  </small>
                                )}
                              </td>
                              <td>
                                <span
                                  title={
                                    isPartnerDriver && req.partnerName
                                      ? `Partner: ${req.partnerName}`
                                      : undefined
                                  }
                                >
                                  {getStatusBadge(req.status)}
                                </span>
                                {isPartnerDriver && req.partnerName && (
                                  <small className="text-muted d-block mt-1">{req.partnerName}</small>
                                )}
                              </td>
                              <td>{new Date(req.createdAt).toLocaleString()}</td>
                              <td onClick={(e) => e.stopPropagation()}>
                                {isPartnerDriver ? (
                                  <span className="text-muted small">Partner-handled</span>
                                ) : (
                                  <select
                                    className="form-select form-select-sm"
                                    value={req.status}
                                    onChange={(e) => handleStatusUpdate(req.id, e.target.value)}
                                    disabled={updatingId === req.id}
                                  >
                                    {DRIVER_COACHING_STATUSES.map((s) => (
                                      <option key={s} value={s}>{STATUS_LABELS[s]}</option>
                                    ))}
                                  </select>
                                )}
                              </td>
                            </tr>
                            {isExpanded && (
                              <tr>
                                <td></td>
                                <td colSpan="8" className="bg-light">
                                  <div className="row g-3 py-2">
                                    <div className="col-md-4">
                                      <label className="text-muted small">Internal Parcel ID</label>
                                      <div className="font-monospace">{req.parcelId}</div>
                                    </div>
                                    <div className="col-md-4">
                                      <label className="text-muted small">BT Shipment ID</label>
                                      <div className="font-monospace">{req.btShipmentId || '-'}</div>
                                    </div>
                                    <div className="col-md-4">
                                      <label className="text-muted small">Metro Code</label>
                                      <div>{req.metroCode || '-'}</div>
                                    </div>
                                    <div className="col-md-4">
                                      <label className="text-muted small">Source</label>
                                      <div>{req.source || '-'}</div>
                                    </div>
                                    <div className="col-md-4">
                                      <label className="text-muted small">Partner</label>
                                      <div>{req.partnerName || '-'}</div>
                                    </div>
                                    <div className="col-md-4">
                                      <label className="text-muted small">Slack Channel</label>
                                      <div className="font-monospace">{req.slackChannelId || '-'}</div>
                                    </div>
                                    <div className="col-12">
                                      <label className="text-muted small">Note</label>
                                      <div>{req.note || <span className="text-muted">No note</span>}</div>
                                    </div>
                                    <div className="col-md-6">
                                      <label className="text-muted small">Updated</label>
                                      <div>{req.updatedAt ? new Date(req.updatedAt).toLocaleString() : '-'}</div>
                                    </div>
                                    <div className="col-md-6">
                                      <label className="text-muted small">Request ID</label>
                                      <div className="font-monospace">#{req.id}</div>
                                    </div>
                                  </div>
                                </td>
                              </tr>
                            )}
                          </React.Fragment>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>

              <div className="d-flex justify-content-between align-items-center mt-3">
                <div className="d-flex align-items-center">
                  <select
                    className="form-select form-select-sm me-2"
                    value={pageSize}
                    onChange={handlePageSizeChange}
                    style={{ width: 'auto' }}
                  >
                    <option value={10}>10 per page</option>
                    <option value={25}>25 per page</option>
                    <option value={50}>50 per page</option>
                    <option value={100}>100 per page</option>
                  </select>
                  <span className="text-muted">
                    {totalItems > 0
                      ? `Showing ${(currentPage - 1) * pageSize + 1} to ${Math.min(currentPage * pageSize, totalItems)} of ${totalItems} entries`
                      : 'No entries'}
                  </span>
                </div>
                <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={handlePageChange} />
              </div>
            </>
          )}
      </div>

      <Footer />
    </div>
  );
}

export default DriverCoaching;